/* Master data (danh mục) — every dropdown list in the portal, in one screen.
 *
 * Max, 2026-08-26: "khi user muốn thêm hay xóa gì thì tôi sẽ biết mà check". The inventory behind
 * this found lists living in two places at once — a table AND an array in the code — which is how
 * task_priority ended up carrying an 'Urgent' that no dropdown ever offered. These tables are now
 * the single copy.
 *
 * The column that makes the screen worth having is "Used": how many live records still point at a
 * value. It is what answers "can I delete this?" without anyone guessing — and a value in use can
 * only be switched off, never deleted, because removing it leaves rows pointing at nothing and the
 * screen then shows a blank where a status used to be.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Database, RefreshCw, Pencil, Trash2, Plus, AlertTriangle, Check,
         ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import Header from './Header';
import { BACKEND_URL } from './backendConfig';
import ModuleHelp from './ModuleHelp';
import './MasterData.css';

/* THE GUIDE (Max 01/11: every module has one). The first catalogue is opened by itself, so the steps can point at its
   table; onBeforeTour closes an add row or an edit left open. */
const TOUR = [
  { selector: '.md-side', title: 'tourMasterData.listT', body: 'tourMasterData.listB' },
  { selector: '.md-head-btns', title: 'tourMasterData.btnsT', body: 'tourMasterData.btnsB' },
  { selector: '.md-toolbar', title: 'tourMasterData.searchT', body: 'tourMasterData.searchB' },
  { selector: '.md-table thead', title: 'tourMasterData.tableT', body: 'tourMasterData.tableB' },
  { selector: '.md-table .md-act', title: 'tourMasterData.rowT', body: 'tourMasterData.rowB' },
];

const API = `${BACKEND_URL}/api/admin/master-data`;

const emptyDraft = (cat) => ({
  name: '', code: '', sortOrder: 0,
  extras: Object.fromEntries((cat?.extras || []).map((e) => [e.col, ''])),
});

const MasterData = () => {
  const { t } = useTranslation();
  const [catalogues, setCatalogues] = useState([]);
  const [selected, setSelected] = useState('');           // selected catalogue key
  const [cat, setCat] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [editId, setEditId] = useState('');
  const [draft, setDraft] = useState({});
  const [adding, setAdding] = useState(false);
  const [newRow, setNewRow] = useState(emptyDraft(null));
  const [search, setSearch] = useState('');
  /* Click-to-sort (Max, 2026-09-08). Three states, not two: ascending, descending, then OFF — and
     "off" is worth a click here because the order the server returns is `sort_order, name`, which is
     the order the DROPDOWNS use. Getting back to "what the user will actually see in the dropdown"
     is the most useful thing a third click can do on this particular screen. */
  const [sort, setSort] = useState({ key: '', dir: 'asc' });

  const loadCatalogues = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const d = await fetch(API, { credentials: 'include' }).then((r) => r.json());
      if (d.error) throw new Error(d.error);
      setCatalogues(d.catalogues || []);
      setSelected((prev) => prev || (d.catalogues || [])[0]?.key || '');
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, []);
  useEffect(() => { loadCatalogues(); }, [loadCatalogues]);

  const loadRows = useCallback(async (key) => {
    if (!key) return;
    setError(''); setEditId(''); setAdding(false);
    try {
      const d = await fetch(`${API}/${key}`, { credentials: 'include' }).then((r) => r.json());
      if (d.error) throw new Error(d.error);
      setCat(d.catalogue); setRows(d.rows || []); setNewRow(emptyDraft(d.catalogue));
    } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { loadRows(selected); }, [selected, loadRows]);
  /* The next catalogue is not the same columns twice. A sort left pointing at an extra column the
     new list does not carry would read every row as blank and silently do nothing. */
  useEffect(() => { setSort({ key: '', dir: 'asc' }); }, [selected]);

  /* Numbers must compare as numbers: Used 4 against 234 sorts backwards the moment either side is
     read as text, and Used is the column most worth sorting on. */
  const sortValue = (r, key) => {
    if (key === 'usedCount' || key === 'sortOrder') return Number(r[key]) || 0;
    if (key === 'isActive') return r.isActive ? 1 : 0;
    if (key === 'updatedAt') return r.updatedAt ? (Date.parse(r.updatedAt) || 0) : 0;
    return String(r[key] ?? '').toLowerCase();
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = !q ? rows
      : rows.filter((r) => [r.name, r.code, ...Object.entries(r).filter(([k]) => k.startsWith('extra_')).map(([, v]) => v)]
        .some((v) => String(v || '').toLowerCase().includes(q)));
    if (!sort.key) return filtered;
    const dir = sort.dir === 'desc' ? -1 : 1;
    // A copy — sorting in place would reorder the `rows` state array that every other read shares.
    return [...filtered].sort((a, b) => {
      const x = sortValue(a, sort.key);
      const y = sortValue(b, sort.key);
      if (x < y) return -dir;
      if (x > y) return dir;
      // Ties fall back to the name. Three values all used once should not shuffle between renders.
      return String(a.name || '').localeCompare(String(b.name || ''));
    });
  }, [rows, search, sort]);

  const toggleSort = (key) => setSort((s) => (
    s.key !== key ? { key, dir: 'asc' }
      : s.dir === 'asc' ? { key, dir: 'desc' }
        : { key: '', dir: 'asc' }));

  /* A plain function, not a component: declaring a component inside the render body gives it a new
     identity every render, which remounts the button and drops focus mid-keyboard-use. */
  const th = (key, label, cls) => (
    <th className={cls} key={key}>
      <button type="button" className={`md-th ${sort.key === key ? 'on' : ''}`}
              title={t('xMasterData.sortBy', { label })} onClick={() => toggleSort(key)}>
        <span>{label}</span>
        {sort.key === key
          ? (sort.dir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)
          : <ChevronsUpDown size={12} className="md-th-idle" />}
      </button>
    </th>
  );

  const send = async (url, options, okMessage) => {
    setBusy(true); setError(''); setNotice('');
    try {
      const res = await fetch(url, { credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...options });
      const d = await res.json().catch(() => ({}));
      /* The server already sends `detail` — the database's own words. Dropping it on the floor is
         why "Could not add the value" was the whole message while the log knew exactly which column
         was refusing, and Max pressed Save eight times (2026-09-08). */
      if (!res.ok) throw new Error([d.error || t('xMasterData.requestFailed', { status: res.status }), d.detail].filter(Boolean).join(' — '));
      setRows(d.rows || []);
      setNotice(okMessage);
      setEditId(''); setAdding(false); setNewRow(emptyDraft(cat));
      loadCatalogues();          // the count in the left-hand list moves too
      return true;
    } catch (e) { setError(e.message); return false; } finally { setBusy(false); }
  };

  const startEdit = (r) => {
    setEditId(String(r.id));
    setDraft({
      name: r.name || '', sortOrder: r.sortOrder || 0, isActive: !!r.isActive,
      extras: Object.fromEntries((cat?.extras || []).map((e) => [e.col, r[`extra_${e.col}`] || ''])),
    });
  };

  const grouped = useMemo(() => {
    const map = new Map();
    catalogues.forEach((c) => {
      if (!map.has(c.group)) map.set(c.group, []);
      map.get(c.group).push(c);
    });
    return [...map.entries()];
  }, [catalogues]);

  return (
    <div>
      <Header />
      <div className="content">
        <div className="md-page">
          <div className="mh-titlerow">
            <div className="md-title">
              <h2><Database size={18} /> {t('xMasterData.title')}</h2>
              <p>
                <Trans t={t} i18nKey="xMasterData.intro" components={{ b: <b /> }} />
              </p>
            </div>
            <ModuleHelp steps={TOUR} tourKey="masterData" ready={!loading && !!cat && rows.length > 0} onBeforeTour={() => { setAdding(false); setEditId(''); }} />
          </div>

          {error && <div className="md-alert error"><AlertTriangle size={15} /> {error}</div>}
          {notice && <div className="md-alert ok">{notice}</div>}

          <div className="md-split">
            <aside className="md-side">
              {loading ? <p className="md-sub">{t('xMasterData.loading')}</p> : grouped.map(([group, items]) => (
                <div className="md-group" key={group}>
                  <div className="md-group-h">{group}</div>
                  {items.map((c) => (
                    <button
                      type="button"
                      key={c.key}
                      className={`md-cat ${selected === c.key ? 'active' : ''}`}
                      onClick={() => { setSelected(c.key); setSearch(''); }}
                    >
                      <span>{c.label}</span>
                      <span className="md-cat-n">{c.count}</span>
                    </button>
                  ))}
                </div>
              ))}
            </aside>

            <section className="md-main">
              {!cat ? <p className="md-sub">{t('xMasterData.pickCatalogue')}</p> : (
                <>
                  <div className="md-head">
                    <div>
                      <h3>{cat.label}</h3>
                      <p className="md-sub">
                        <code>{cat.table}</code>
                        {cat.usedIn?.length ? (
                          <>
                            {' · '}
                            <Trans t={t} i18nKey="xMasterData.usedBy" values={{ tables: cat.usedIn.join(', ') }} components={{ c: <code /> }} />
                          </>
                        ) : null}
                      </p>
                    </div>
                    <div className="md-head-btns">
                      <button type="button" className="md-btn" onClick={() => loadRows(selected)}>
                        <RefreshCw size={13} /> {t('xMasterData.refresh')}
                      </button>
                      <button type="button" className="md-btn primary" disabled={adding} onClick={() => setAdding(true)}>
                        <Plus size={13} /> {t('xMasterData.addValue')}
                      </button>
                    </div>
                  </div>

                  <div className="md-toolbar">
                    <input
                      className="md-input"
                      placeholder={t('xMasterData.searchPh')}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                    <span className="md-sub">
                      {visible.length !== rows.length
                        ? t('xMasterData.countOf', { n: visible.length, total: rows.length })
                        : t('xMasterData.countN', { n: visible.length })}
                    </span>
                  </div>

                  <div className="md-scroll">
                  <table className="md-table">
                    <thead>
                      <tr>
                        {th('name', t('xMasterData.col.value'))}
                        {cat.hasCode && th('code', t('xMasterData.col.code'), 'md-w-code')}
                        {(cat.extras || []).map((e) => th(`extra_${e.col}`, e.label, 'md-w-extra'))}
                        {cat.hasSort && th('sortOrder', t('xMasterData.col.order'), 'md-w-sort')}
                        {th('usedCount', t('xMasterData.col.used'), 'md-w-used')}
                        {cat.hasActive && th('isActive', t('xMasterData.col.active'), 'md-w-active')}
                        {th('updatedAt', t('xMasterData.col.lastChange'), 'md-w-by')}
                        {/* Action holds buttons, not a value — there is nothing to order it by. */}
                        <th className="md-w-act">{t('xMasterData.col.action')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adding && (
                        <tr className="md-new">
                          <td>
                            <input className="md-input" autoFocus placeholder={t('xMasterData.newValuePh')}
                                   value={newRow.name} onChange={(e) => setNewRow({ ...newRow, name: e.target.value })} />
                          </td>
                          {cat.hasCode && (
                            <td><input className="md-input" placeholder={t('xMasterData.codePh')}
                                       value={newRow.code} onChange={(e) => setNewRow({ ...newRow, code: e.target.value })} /></td>
                          )}
                          {(cat.extras || []).map((e) => (
                            <td key={e.col}>
                              <input className="md-input" placeholder={e.label}
                                     value={newRow.extras[e.col] || ''}
                                     onChange={(ev) => setNewRow({ ...newRow, extras: { ...newRow.extras, [e.col]: ev.target.value } })} />
                            </td>
                          ))}
                          {cat.hasSort && (
                            <td><input className="md-input" type="number" value={newRow.sortOrder}
                                       onChange={(e) => setNewRow({ ...newRow, sortOrder: e.target.value })} /></td>
                          )}
                          <td className="md-num">—</td>
                          {cat.hasActive && <td className="md-mid">—</td>}
                          <td />
                          <td className="md-act">
                            <button type="button" className="md-btn primary" disabled={busy || !newRow.name.trim()}
                                    onClick={() => send(`${API}/${selected}`, { method: 'POST', body: JSON.stringify(newRow) }, t('xMasterData.added', { name: newRow.name.trim() }))}>
                              {busy ? t('xMasterData.saving') : t('xMasterData.save')}
                            </button>
                            <button type="button" className="md-btn" disabled={busy} onClick={() => setAdding(false)}>{t('xMasterData.cancel')}</button>
                          </td>
                        </tr>
                      )}
                      {visible.map((r) => {
                        const editing = editId === String(r.id);
                        const used = Number(r.usedCount) || 0;
                        return (
                          <tr key={r.id} className={[editing ? 'md-editing' : '', r.isActive ? '' : 'md-off'].filter(Boolean).join(' ')}>
                            <td>
                              {editing
                                ? <input className="md-input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
                                : <span className="md-name">{r.name}</span>}
                            </td>
                            {cat.hasCode && <td className="md-code">{r.code || '—'}</td>}
                            {(cat.extras || []).map((e) => (
                              <td key={e.col}>
                                {editing
                                  ? <input className="md-input" value={draft.extras[e.col] || ''}
                                           onChange={(ev) => setDraft({ ...draft, extras: { ...draft.extras, [e.col]: ev.target.value } })} />
                                  : (r[`extra_${e.col}`] || '—')}
                              </td>
                            ))}
                            {cat.hasSort && (
                              <td className="md-num">
                                {editing
                                  ? <input className="md-input" type="number" value={draft.sortOrder}
                                           onChange={(e) => setDraft({ ...draft, sortOrder: e.target.value })} />
                                  : r.sortOrder}
                              </td>
                            )}
                            <td className={`md-num ${used ? 'md-used' : ''}`}>{used}</td>
                            {cat.hasActive && (
                              <td className="md-mid">
                                {/* A disabled checkbox renders grey in every browser and ignores accent-color,
                                    so a row that is switched ON looked switched off. Only the row being edited
                                    gets a real control; the rest get a mark that actually reads as on or off. */}
                                {editing ? (
                                  <input
                                    type="checkbox"
                                    className="md-check"
                                    checked={!!draft.isActive}
                                    onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
                                  />
                                ) : r.isActive ? (
                                  <span className="md-mark on" aria-label={t('xMasterData.activeAria')}><Check size={13} strokeWidth={3.5} /></span>
                                ) : (
                                  <span className="md-mark off" aria-label={t('xMasterData.switchedOff')} />
                                )}
                              </td>
                            )}
                            <td className="md-by">
                              {r.updatedBy || '—'}
                              {r.updatedAt ? <span className="md-when">{String(r.updatedAt).slice(0, 10)}</span> : null}
                            </td>
                            <td className="md-act">
                              {editing ? (
                                <>
                                  <button type="button" className="md-btn primary" disabled={busy}
                                          onClick={() => send(`${API}/${selected}/${encodeURIComponent(r.id)}`, { method: 'PUT', body: JSON.stringify(draft) }, t('xMasterData.saved', { name: draft.name }))}>
                                    {busy ? t('xMasterData.saving') : t('xMasterData.save')}
                                  </button>
                                  <button type="button" className="md-btn" disabled={busy} onClick={() => setEditId('')}>{t('xMasterData.cancel')}</button>
                                </>
                              ) : (
                                <>
                                  <button type="button" className="md-btn" disabled={!!editId || adding} onClick={() => startEdit(r)}>
                                    <Pencil size={12} /> {t('xMasterData.edit')}
                                  </button>
                                  <button
                                    type="button"
                                    className="md-btn danger"
                                    disabled={busy || used > 0 || !!editId}
                                    title={used > 0
                                      ? t('xMasterData.usedTitle', { n: used })
                                      : t('xMasterData.deleteTitle')}
                                    onClick={() => {
                                      // eslint-disable-next-line no-alert
                                      if (window.confirm(t('xMasterData.confirmDelete', { name: r.name }))) {
                                        send(`${API}/${selected}/${encodeURIComponent(r.id)}`, { method: 'DELETE' }, t('xMasterData.deleted', { name: r.name }));
                                      }
                                    }}
                                  >
                                    <Trash2 size={12} />
                                  </button>
                                </>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {!visible.length && (
                        <tr><td colSpan={9} className="md-empty">{t('xMasterData.empty')}</td></tr>
                      )}
                    </tbody>
                  </table>
                  </div>
                </>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MasterData;
